import * as B from '@blueprintjs/core';
import {DateRange} from '@blueprintjs/datetime2';
import {ChartData, LegendItem, Plugin} from 'chart.js';
import React from 'react';

import {
  EXPANDED_GRAPH_FONT_SIZE,
  MEDIUM_GRAPH_FONT_SIZE,
  useHtmlLegend,
} from 'app/components/AnalyzePolygonChart/utils';
import {DashboardWidget} from 'app/components/Dashboard/DashboardWidget';
import GenericPieChart from 'app/components/Dashboard/GenericChart/GenericPieChart';
import {PieGroup, PieGroupControls} from 'app/components/Dashboard/GenericChart/PieGroupControls';
import * as cs from 'app/components/Dashboard/MultiFeatureAnalysisWidget/MultiFeatureAnalysis.styl';
import {formatPieChartTooltip, handleDownloadPng} from 'app/components/Dashboard/utils/chartUtils';
import {makeTopPieChartEntries} from 'app/components/Dashboard/utils/chartUtils';
import LoadingOverlayWrapper from 'app/components/LoadingOverlayWrapper';
import {usePushNotification} from 'app/components/Notification';
import {ChartConfig} from 'app/src/components/Dashboard/types';

/** This widget displays a pie chart, based on the data and pie groupings we pass in.
 */
export interface BaseLensStatsWidgetProps {
  error: Error | null | undefined;
  widgetConfig: ChartConfig;
  plugins?: Plugin<'pie' | 'doughnut'>[];
  loading: boolean;
  showControls: boolean;
  pieGroups: PieGroup[];
  selectedPieGroup: PieGroup;
  setSelectedPieGroup: React.Dispatch<React.SetStateAction<PieGroup>>;
  chartData: ChartData<'pie' | 'doughnut'>;
  countItemLabel: string;
  showDateRangeInput: boolean;
  dateRange?: DateRange;
  setDateRange?: (dateRange: DateRange) => void;
  percentLoaded: number | undefined;
}

export const BaseLensStatsWidget: React.FC<BaseLensStatsWidgetProps> = ({
  widgetConfig,
  plugins,
  loading,
  showControls,
  pieGroups,
  selectedPieGroup,
  setSelectedPieGroup,
  chartData,
  countItemLabel,
  showDateRangeInput,
  dateRange,
  setDateRange,
  percentLoaded,
}) => {
  const chartDownloadRef = React.useRef<HTMLDivElement>(null);
  const pushNotification = usePushNotification();

  const pushDownloadError = () => {
    pushNotification({
      message: 'Error downloading chart',
      options: {
        intent: B.Intent.DANGER,
      },
    });
  };
  const [isExpanded, setIsExpanded] = React.useState(false);

  // Filter chart data to show top entries based on expanded state
  const filteredChartData = React.useMemo(() => {
    if (!chartData.labels || !chartData.datasets?.[0]?.data) {
      return chartData;
    }

    const entries = makeTopPieChartEntries(
      chartData.labels.map((label, index) => [String(label), chartData.datasets[0].data[index]]),
      5,
      isExpanded
    );

    return {
      ...chartData,
      labels: entries.map(([label]) => label),
      datasets: [
        {
          ...chartData.datasets[0],
          data: entries.map(([, value]) => value),
        },
      ],
    };
  }, [chartData, isExpanded]);

  const {renderLegend, legendItems, htmlLegendPlugin} = useHtmlLegend();

  const renderChartLegend = () => {
    return renderLegend(
      legendItems,
      false,
      false, // isExpanded is now handled in BaseLensStatsWidget
      //TODO(eva): would be fun to add some cool styling on hover in the future
      // but for right now dont pass any custom onHover/mouseOut handlers
      () => {},
      () => {},
      () => {},
      {
        fontSize: isExpanded ? EXPANDED_GRAPH_FONT_SIZE : MEDIUM_GRAPH_FONT_SIZE,
        alignContent: 'flex-start',
        flexDirection: isExpanded ? 'row' : 'column',
        justifyContent: 'flex-start',
      }
    );
  };

  const downloadLegendItems: LegendItem[] | undefined = React.useMemo(() => {
    return legendItems?.map((item, index) => ({
      text: item.text,
      fillStyle:
        typeof item.fillStyle === 'string'
          ? item.fillStyle
          : typeof item.strokeStyle === 'string'
            ? item.strokeStyle
            : '#000000',
      hidden: false,
      datasetIndex: index,
    }));
  }, [legendItems]);

  const dateLabels = React.useMemo(() => {
    if (!dateRange || !setDateRange || dateRange.every((date) => !date)) return '';

    return ` from ${new Date(dateRange[0]?.toISOString() ?? '').toLocaleDateString()} to ${new Date(dateRange[1]?.toISOString() ?? '').toLocaleDateString()}`;
  }, [dateRange, setDateRange]);

  // Check if we have any data in our filtered chart
  const hasData = React.useMemo(() => {
    return !loading && chartData?.labels?.[0] !== 'No data';
  }, [chartData, loading]);

  // calculate the total count for display
  const totalCount = React.useMemo(() => {
    const data = filteredChartData.datasets?.[0]?.data;
    if (!data) return 0;
    return data.reduce((a: number, b: number) => a + b, 0);
  }, [filteredChartData]);

  const chartTitle = hasData
    ? `${totalCount.toLocaleString(undefined, {maximumFractionDigits: 2})} ${countItemLabel}`
    : `0 ${countItemLabel}`;

  return (
    <DashboardWidget
      title={widgetConfig.title + dateLabels}
      size={isExpanded ? 'large' : 'small'}
      controls={
        <div className={cs.widgetControlsContainer}>
          <B.Tooltip content={isExpanded ? 'Hide details' : 'Show details'}>
            <B.AnchorButton
              icon={isExpanded ? 'minimize' : 'maximize'}
              minimal
              small
              onClick={() => setIsExpanded((prev) => !prev)}
            />
          </B.Tooltip>
          <B.Popover
            content={
              <B.Menu>
                <B.MenuItem
                  icon="download"
                  text="Download chart as PNG"
                  disabled={!hasData}
                  onClick={() =>
                    handleDownloadPng(
                      chartDownloadRef.current?.querySelector('canvas'),
                      widgetConfig,
                      undefined,
                      isExpanded,
                      pushDownloadError,
                      downloadLegendItems
                    )
                  }
                />
              </B.Menu>
            }
            placement="bottom-end"
            minimal
          >
            <B.Button icon="more" minimal small />
          </B.Popover>
        </div>
      }
    >
      <LoadingOverlayWrapper
        message={'Loading...'}
        loading={loading}
        loadingIndicator={<B.Spinner intent={B.Intent.PRIMARY} value={percentLoaded} />}
      >
        <div style={{height: '100%'}} ref={chartDownloadRef}>
          <GenericPieChart
            renderLegend={renderChartLegend}
            controls={
              showControls && (
                <div>
                  {showDateRangeInput && dateRange && setDateRange ? (
                    <PieGroupControls
                      pieGroups={pieGroups}
                      selectedPieGroup={selectedPieGroup}
                      setSelectedPieGroup={setSelectedPieGroup}
                      showDateRangeInput={true}
                      dateRange={dateRange}
                      setDateRange={setDateRange}
                    />
                  ) : (
                    <PieGroupControls
                      pieGroups={pieGroups}
                      selectedPieGroup={selectedPieGroup}
                      setSelectedPieGroup={setSelectedPieGroup}
                      showDateRangeInput={false}
                    />
                  )}
                </div>
              )
            }
            type="doughnut"
            options={{
              plugins: {
                tooltip: {
                  enabled: hasData,
                  bodyFont: {
                    size: isExpanded ? EXPANDED_GRAPH_FONT_SIZE : MEDIUM_GRAPH_FONT_SIZE,
                  },
                  position: 'average',
                  callbacks: {
                    label: (context) =>
                      formatPieChartTooltip(context, selectedPieGroup, countItemLabel),
                  },
                },
                legend: {
                  display: false, // we rendering our own legend
                },
              },
            }}
            labels={filteredChartData.labels}
            datasets={filteredChartData.datasets}
            plugins={plugins ? [...plugins, htmlLegendPlugin] : [htmlLegendPlugin]}
            displayTitle={chartTitle}
            isExpanded={isExpanded}
          />
        </div>
      </LoadingOverlayWrapper>
    </DashboardWidget>
  );
};
